"use client";

import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";

const fetcher = (url: string) => fetch(url).then((res) => res.json());

export default function PlatformUserCount({
  displayMode = "short",
  onlineRatio = 0.2725,
  /**
   * Which surface this sits on. Only "online/offline" cares — its label colour
   * and the muted dot beside the offline figure are painted in, so on a light
   * panel both disappear. Defaults to the dark treatment every existing caller
   * already gets.
   */
  theme = "dark",
  className,
}: {
  displayMode?: "short" | "full" | "online/offline";
  onlineRatio?: number;
  theme?: "dark" | "light";
  className?: string;
}) {
  function formatNumber(num: number, short?: boolean) {
    if (!short) return num.toLocaleString();

    if (num >= 1_000_000)
      return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
    if (num >= 1_000) return (num / 1_000).toFixed(0) + "k";
    return num.toString();
  }

  const { data, error, isLoading } = useQuery({
    queryKey: ["platform-usercount"],
    queryFn: () => fetcher("/api/platform-usercount"),
    staleTime: 10 * 60 * 1000, // 10 minutes - data stays fresh
    gcTime: 15 * 60 * 1000, // 15 minutes - cache retention
    refetchInterval: 10 * 60 * 1000, // auto-refetch every 10 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    placeholderData: {count: 60000},
  });

  const count =
    !isLoading && !error && typeof data?.count === "number"
      ? data.count
      : "60000";

  if (displayMode === "short") {
    return (
      <p className={cn(
        "mb-0 leading-none text-[2rem]",
        className
      )}>
        {formatNumber(count, true)}
      </p>
    );
  }

  if (displayMode === "full") {
    return (
      <p className={cn(
        "mb-0 leading-none text-[2rem]",
        className
      )}>
        {formatNumber(count, false)}
      </p>
    );
  }

  // mode === "online/offline"
  const online = Math.round(count * onlineRatio);
  const offline = Math.max(count - online, 0);

  return (
    <div className={cn(
      "inline-flex items-center gap-[1rem]",
      theme === "light" ? "prose-p:text-black/70" : "prose-p:text-white/60",
      className
    )}>
      <div className="flex items-center gap-[1rem]">
        <div className="w-[1rem] h-[1rem] rounded-full flex-shrink-0 bg-[#61FFCE]" />
        <p className="mb-0 leading-none text-[2rem]">{online.toLocaleString()} Online</p>
      </div>
      <div className="flex items-center gap-[1rem]">
        <div className={cn(
          "w-[1rem] h-[1rem] rounded-full flex-shrink-0",
          theme === "light" ? "bg-black/20" : "bg-white/20"
        )} />
        <p className="mb-0 leading-none text-[2rem]">{offline.toLocaleString()} Offline</p>
      </div>
    </div>
  );
}